// src/components/Navbar.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, CreditCard, Sparkles, Images, Wallet, LayoutDashboard, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useUserData } from '../hooks/useUserData';
import { useAdminGate } from '../hooks/useAdminGate';
import { createApi } from '../lib/api';

function classNames(...a) {
  return a.filter(Boolean).join(' ');
}

function Initials({ name = '' }) {
  const parts = String(name).trim().split(/\s+/);
  const initials = parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || 'U';
  return (
    <div className="grid place-items-center w-8 h-8 rounded-full bg-white/10 text-xs font-semibold">
      {initials}
    </div>
  );
}

export default function Navbar() {
  const { user, authReady, signOut } = useAuth();
  const { credits } = useUserData();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [aiSolutionsOpen, setAiSolutionsOpen] = useState(false);
  const [mobileAiSolutionsOpen, setMobileAiSolutionsOpen] = useState(false);
  const { loading: adminLoading, isAdmin } = useAdminGate();
  const menuRef = useRef(null);
  const aiSolutionsRef = useRef(null);
  const { pathname } = useLocation();

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
    setAiSolutionsOpen(false);
    setMobileAiSolutionsOpen(false);
  }, [pathname]);

  // Close profile menu on outside click/escape
  useEffect(() => {
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (aiSolutionsRef.current && !aiSolutionsRef.current.contains(e.target)) setAiSolutionsOpen(false);
    }
    function onEsc(e) {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setAiSolutionsOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  // AI Solutions dropdown items
  const aiSolutionsItems = [
    { to: '/app', label: 'AI Hairstyle' },
    { to: '/random-hairstyle-generator', label: 'Random Hairstyle Generator' },
    { to: '/which-hairstyle-suits-me-male-female', label: 'Face Shape Analyzer' },
    { to: '/hair-ingredient-checker', label: 'Hair Ingredient Checker' },
    { to: '/norwood-hamilton-scale-will-i-go-bald-calculator', label: 'Baldness Calculator' },
  ];

  // Main nav items (Baldness Calculator moved to dropdown)
  const navItems = [
    { to: '/creations', label: 'My Creations' },
    { to: '/about', label: 'About' },
    { to: '/styles', label: 'Style Library' },
    { to: '/shop', label: 'Shop' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/hair-specialist', label: 'Find Pros' },
    { to: '/blog', label: 'Blog' },
  ];

  const LinkItem = ({ to, label }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        classNames(
          'px-2 py-1 rounded-md text-sm hover:text-white/90',
          isActive ? 'text-white' : 'text-white/80'
        )
      }
    >
      {label}
    </NavLink>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0b0b12]/70 backdrop-blur">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        {/* Left: Brand */}
        <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight group">
          <img src="/logo.png" alt="AI Hairstyle Studio logo" className="transition-transform group-hover:scale-110 h-10 w-10 shrink-0" />
          <span className="align-middle">AI Hairstyle <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-500 to-cyan-400">Studio</span></span>
        </Link>

        {/* Center: Desktop nav */}
        <nav className="hidden md:flex items-center gap-4">
          {/* AI Solutions Dropdown */}
          <div className="relative" ref={aiSolutionsRef}>
            <button
              onClick={() => setAiSolutionsOpen(v => !v)}
              className={classNames(
                'px-2 py-1 rounded-md text-sm hover:text-white/90 flex items-center gap-1',
                aiSolutionsOpen ? 'text-white' : 'text-white/80'
              )}
              aria-haspopup="menu"
              aria-expanded={aiSolutionsOpen}
            >
              AI Solutions
              <svg className={classNames('w-4 h-4 transition-transform', aiSolutionsOpen && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {aiSolutionsOpen && (
              <div
                role="menu"
                className="absolute left-0 mt-2 w-64 rounded-xl border border-white/10 bg-[#101018] shadow-xl p-1 z-50"
              >
                {aiSolutionsItems.map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="block px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-white/80 hover:text-white"
                    role="menuitem"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {navItems.map(i => <LinkItem key={i.to} {...i} />)}
        </nav>

        {/* Right: Auth/credits */}
        <div className="flex items-center gap-3">
          {/* Credits pill (hide until authReady known) */}
          {authReady && user && (
            <Link
              to="/billing"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-1 text-xs"
              title="Your credits"
            >
              <Wallet size={14} />
              <span className="font-semibold">{credits ?? 0}</span>
            </Link>
          )}

          {/* Desktop: auth UI */}
          <div className="hidden md:block">
            {!authReady ? (
              <div className="w-20 h-8 rounded-md bg-white/10 animate-pulse" />
            ) : user ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(v => !v)}
                  className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2 py-1 hover:bg-white/10"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  {/* Avatar or initials */}
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || user.email || 'profile'}
                      className="w-8 h-8 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <Initials name={user.displayName || user.email} />
                  )}
                  <span className="max-w-[120px] truncate text-sm opacity-90">
                    {user.displayName || user.email}
                  </span>
                </button>

                {menuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-[#101018] shadow-xl p-1"
                  >
                    <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5" role="menuitem">
                      <LayoutDashboard size={16} /> Dashboard
                    </Link>

                    <Link to="/app" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5" role="menuitem">
                      <Sparkles size={16} /> AI Hairstyle
                    </Link>

                    {/* ✅ My Account in desktop dropdown */}
                    <Link to="/account" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5" role="menuitem">
                      <User size={16} /> My Account
                    </Link>

                    <Link to="/creations" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5" role="menuitem">
                      <Images size={16} /> My Creations
                    </Link>
                    <Link to="/billing" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5" role="menuitem">
                      <CreditCard size={16} /> Billing
                    </Link>

                    {/* 🔐 Admin-only link (hide while loading, show only if admin) */}
                    {!adminLoading && isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5"
                        role="menuitem"
                      >
                        <Crown size={16} />
                        Admin Panel
                      </Link>
                    )}

                    <div className="my-1 h-px bg-white/10" />
                    <button
                      onClick={signOut}
                      className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5"
                      role="menuitem"
                    >
                      <LogOut size={16} /> Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="rounded-md bg-white text-black px-3 py-1.5 text-sm font-semibold hover:bg-gray-200"
              >
                Sign in
              </Link>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 rounded-md hover:bg-white/10"
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#0b0b12]">
          <div className="px-4 py-3 flex flex-col gap-2">
            {/* AI Solutions collapsible */}
            <div>
              <button
                onClick={() => setMobileAiSolutionsOpen(v => !v)}
                className="w-full flex items-center justify-between px-2 py-2 rounded-md text-sm text-white/80 hover:bg-white/5"
              >
                <span>AI Solutions</span>
                <svg className={classNames('w-4 h-4 transition-transform', mobileAiSolutionsOpen && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {mobileAiSolutionsOpen && (
                <div className="ml-4 mt-1 flex flex-col gap-1">
                  {aiSolutionsItems.map(item => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        classNames(
                          'px-2 py-2 rounded-md text-sm',
                          isActive ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5'
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
            {navItems.map(i => (
              <NavLink
                key={i.to}
                to={i.to}
                className={({ isActive }) =>
                  classNames(
                    'px-2 py-2 rounded-md text-sm',
                    isActive ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5'
                  )
                }
              >
                {i.label}
              </NavLink>
            ))}

            {/* ✅ My Account in mobile panel (only when signed in) */}
            {authReady && user && (
              <>
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    classNames(
                      'px-2 py-2 rounded-md text-sm',
                      isActive ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5'
                    )
                  }
                >
                  Dashboard
                </NavLink>
                <NavLink
                  to="/account"
                  className={({ isActive }) =>
                    classNames(
                      'px-2 py-2 rounded-md text-sm',
                      isActive ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5'
                    )
                  }
                >
                  My Account
                </NavLink>
              </>
            )}

            {/* 🔐 Admin-only link in mobile panel (hide while loading, show only if admin) */}
            {!adminLoading && isAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  classNames(
                    'px-2 py-2 rounded-md text-sm',
                    isActive ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5'
                  )
                }
              >
                Admin Panel
              </NavLink>
            )}

            {/* Auth area */}
            {!authReady ? (
              <div className="h-9 rounded-md bg-white/10 animate-pulse mt-2" />
            ) : user ? (
              <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-white/10 p-2">
                <div className="flex items-center gap-2">
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || user.email}
                      className="w-9 h-9 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <Initials name={user.displayName || user.email} />
                  )}
                  <div className="text-sm">
                    <div className="truncate max-w-[140px]">{user.displayName || user.email}</div>
                    <div className="text-xs opacity-70">Credits: {credits ?? 0}</div>
                  </div>
                </div>
                <button
                  onClick={signOut}
                  className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-xs hover:bg-white/5"
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="mt-2 inline-flex items-center justify-center rounded-md bg-white text-black px-3 py-2 text-sm font-semibold hover:bg-gray-200"
              >
                <User size={16} className="mr-2" />
                Sign in
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

